/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { Fragment, useCallback, useMemo } from 'react';
import { QuestionListEmpty } from '../survey-question-list/partials/QuestionListEmpty';
import { SurveyQuestionListProps } from '../survey-question-list/survey-question-list';
import { getQuestionGroupId, getTotalQuestionCount } from '../survey-question-list/utils';
import { GroupQuestionList } from './GroupQuestionList';
import { SurveyGroupTitle } from './SurveyGroupTitle';
import { GroupQuestionListColumns, GroupHeaderColumn } from './types';

interface Props extends Pick<SurveyQuestionListProps, 'surveyGroups' | 'alternativeCode'> {
  headerColumns?: GroupHeaderColumn[];
  columns?: GroupQuestionListColumns;
  toggleQuestionGroup: (questionGroupId: string) => void;
  questionGroupDisplay: string[];
}

export const SurveyGroupList = (props: Props) => {
  const { surveyGroups, toggleQuestionGroup, alternativeCode, questionGroupDisplay, columns } = props;

  const singleGroup = surveyGroups.length === 1;
  const questionCount = useMemo(
    () => surveyGroups.reduce((acc, questionGroup) => acc + questionGroup.list.length, 0),
    [surveyGroups]
  );

  const isGroupOpen = useCallback((questionGroupId: string) => {
    return singleGroup || questionGroupDisplay.includes(questionGroupId);
  }, [singleGroup, questionGroupDisplay]);

  if (questionCount === 0) {
    return <QuestionListEmpty />;
  }

  return (
    <>
      {columns && columns.length > 0 ? (
        <div className='g17-thead'>
          <div className='g17-tr d-flex py-1 mb-1'>
            {columns.map((column, index) => {
              return column.header ? React.createElement(column.header, { key: `thead-col-${index}` }) : null;
            })}
          </div>
        </div>
      ) : null}
      {surveyGroups.map((questionGroup, i) => {
        const questionList = questionGroup.list;
        const questionCount = getTotalQuestionCount(questionGroup);
        const isRenderedTitle = questionCount > 0;
        const questionGroupId = getQuestionGroupId(i);
        const isOpen = isGroupOpen(questionGroupId);
        const headerColumns = props.headerColumns ?? [];

        return (
          <Fragment key={`group-${i}-${questionGroup.groupName}`}>
            {isRenderedTitle ? (
              <SurveyGroupTitle
                isOpen={isOpen}
                toggle={singleGroup ? undefined : () => toggleQuestionGroup(questionGroupId)}
                questionGroup={questionGroup}
                questionList={questionList}
                headerColumns={headerColumns}
              />
            ) : null}
            {isOpen ? (
              questionGroup.subGroups ? (
                questionGroup.subGroups.map((subGroup, ix) => {
                  if (!subGroup.list || subGroup.list.length === 0) {
                    return null;
                  }
                  const questionList = subGroup.list;
                  const columnProps = {
                    questionList,
                    questionGroup: subGroup,
                  };
                  return (
                    <Fragment key={`subgroup-${ix}-${subGroup.code}`}>
                      {subGroup.code !== '' ? (
                        // Questions that don't have subgroup tags associated
                        <div className='ml-3 sub__group__title d-flex align-items-center justify-content-between'>
                          <div>{subGroup.name}</div>
                          <div className='d-flex align-items-center justify-content-between'>
                            {headerColumns.map((element, index) =>
                              React.createElement(element, {
                                ...columnProps,
                                key: `subgroup-col-${index}-${subGroup.code}`,
                              })
                            )}
                          </div>
                        </div>
                      ) : null}
                      <GroupQuestionList
                        {...props}
                        questionGroupId={questionGroupId}
                        questionList={questionList}
                        alternativeCode={
                          alternativeCode ?? questionGroup.groupData?.alternativeCode ?? questionGroup.groupCode
                        }
                        preferredAltCodes={questionGroup.groupData?.preferredAltCodes}
                        colour={questionGroup.groupData && questionGroup.groupData.colour}
                      />
                    </Fragment>
                  );
                })
              ) : (
                <GroupQuestionList
                  {...props}
                  questionGroupId={questionGroupId}
                  questionList={questionList}
                  alternativeCode={
                    alternativeCode ?? questionGroup.groupData?.alternativeCode ?? questionGroup.groupCode
                  }
                  preferredAltCodes={questionGroup.groupData?.preferredAltCodes}
                  colour={questionGroup.groupData && questionGroup.groupData.colour}
                />
              )
            ) : null}
          </Fragment>
        );
      })}
    </>
  );
};
